import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function TraeterrassePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const galleryImages = [
    {
      src: "/_assets/traeterrasse-hero.webp",
      title: "Ny træterrasse tilpasset bolig og have",
      category: "Træterrasse",
      desc: "Træterrasse med smukke naturlige træbrædder, præcis tilpasning og varmt udtryk."
    },
    {
      src: "/_assets/traeterrasse-have.webp",
      title: "Træterrasse i flere niveauer",
      category: "Have & Uderum",
      desc: "Skræddersyet niveaudeling med integrerede trin på en skrånende grund."
    },
    {
      src: "/_assets/traeterrasse-ved-hus.webp",
      title: "Træterrasse privat ved hus",
      category: "Privat terrasse",
      desc: "Naturlig overgang mellem hus og have med solid underopbygning."
    },
    {
      src: "/_assets/glashegn.webp",
      title: "Terrasse med glasafskærmning",
      category: "Afskærmning",
      desc: "Terrasseløsning kombineret med læhegn for optimal udnyttelse af uderummet."
    }
  ];

  const priceFactors = [
    {
      title: "Terrassens størrelse, form og ønskede træsort",
      desc: "Valg af træsort (f.eks. trykimprægneret, lærk, hårdttræ som ipé eller cumaru) og geometri.",
      icon: "fa-tree"
    },
    {
      title: "Underlagets tilstand og behov for forberedelse",
      desc: "Udgravning, stabilgrus, punktfundamenter eller tilpasning til eksisterende fliseunderlag.",
      icon: "fa-cubes"
    },
    {
      title: "Terrassens højde og eventuelle niveauforskelle",
      desc: "Hævet konstruktion, trappetrin og tilpasning til grundens fald og husets sokkelhøjde.",
      icon: "fa-stairs"
    },
    {
      title: "Behov for trin, afslutninger eller overdækning",
      desc: "Geringer, friser, gelændere, indbygget belysning eller delvis overdækning af opholdszonen.",
      icon: "fa-ruler-combined"
    },
    {
      title: "Adgangsforhold og arbejdets samlede omfang",
      desc: "Tilgængelighed for materialer, maskiner samt eventuel fjernelse af gammel belægning.",
      icon: "fa-truck"
    }
  ];

  return (
    <>
      <SEO
        title="Træterrasse med flotte detaljer | Få pris på træterrasse her"
        description="Hvad koster en træterrasse? Få en løsning tilpasset din grund, bolig og ønsker. Hovendals ApS bygger træterrasser på Fyn og i hele Danmark."
        ogImage="/_assets/traeterrasse-hero.webp"
      />

      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[620px] flex items-center bg-[#143463] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl("/_assets/traeterrasse-hero.webp", "width=1920,quality=85,format=auto")}
            alt="Ny træterrasse tilpasset dine ønsker og din bolig"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#143463]/95 via-[#143463]/75 to-transparent"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 w-full">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 text-[#C2CCDF] text-xs font-semibold uppercase tracking-wider">
              <i className="fa-sharp fa-solid fa-tree text-[#004FA4]" aria-hidden="true"></i>
              <span>Uderum & Terrasser</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.1]">
              Træterrasse tilpasset dine ønsker og din bolig
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 font-light leading-relaxed">
              Drømmer du om en ny træterrasse, hvor hverdagen kan rykke udenfor? Hos Hovendals tager vi udgangspunkt i dine ønsker og forholdene på stedet. Vi hjælper både private og erhverv med at skabe et uderum, der passer til den måde, det skal bruges på, og det udtryk, du ønsker.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Indhent tilbud</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
                <span>Ring {companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Introduktion */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Naturlig overgang
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                En naturlig ramme om udelivet
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  En træterrasse kan skabe en naturlig overgang mellem boligen og haven og give mere plads til ophold, måltider og afslapning udenfor. Placering, størrelse, materialevalg og underlag har betydning for det færdige resultat, og derfor tilpasser vi løsningen til de konkrete forhold på din grund.
                </p>
                <p>
                  Uanset om du ønsker en klassisk terrasse i niveau med stuen, en hævet terrasse eller et hyggeligt uderum længere ude i haven, sikrer vi en gennemtænkt konstruktion udført i solide materialer.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group"
                >
                  <span>Tag en uforpligtende snak om dine terrasseidéer</span>
                  <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative border border-[#D6D3CE]/60 p-3 bg-white shadow-lg">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={imageUrl("/_assets/traeterrasse-hero.webp", "width=800,quality=85,format=auto")}
                    alt="Ny træterrasse i høj håndværksmæssig kvalitet"
                    className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 bg-[#F3F4F7] mt-3 flex items-center justify-between text-xs text-[#143463]">
                  <span className="font-medium">Naturlige træbrædder</span>
                  <span className="text-[#4B515D]">Skræddersyet efter mål</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 2: Hvordan skal din nye træterrasse bruges? */}
      <section className="py-20 md:py-24 bg-[#F3F4F7] border-y border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Funktion & Anvendelse
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight">
              Hvordan skal din nye træterrasse bruges?
            </h2>
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              Før vi taler om brædder og mål, ser vi på, hvordan du vil bruge terrassen. Skal den være samlingspunkt for familien, et roligt hjørne i haven eller et udeområde ved virksomheden? Plads til møbler, adgang fra huset og solens placering er med til at afgøre både størrelse og form.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-tree" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Træsort & løbende pleje
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Vi taler også om træsort, bræddernes retning og den pleje, du ønsker at bruge tid på. Træ er et levende materiale, som ændrer udtryk med vind og vejr og kræver løbende vedligeholdelse.
              </p>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Hvis du foretrækker et materiale med mindre behov for løbende pleje, kan en <Link to="/kompositterrasse" className="text-[#004FA4] font-medium underline hover:text-[#143463]">kompositterrasse</Link> være værd at overveje.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-compass" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Fritstående terrasse
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Terrassen behøver heller ikke ligge traditionelt op ad huset. En fritstående træterrasse kan eksempelvis skabe et separat opholdsområde længere ude i haven.
              </p>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Det giver mulighed for at udnytte aftensolen eller et uforstyrret læhjørne på grunden, hvor naturen kan nydes i fred og ro.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-umbrella" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Overdækning & helhed
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                En træterrasse med overdækning giver mulighed for at bruge uderummet i flere typer vejr og forlænge sæsonen fra det tidlige forår til det sene efterår.
              </p>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Skal området omkring boligen tænkes mere samlet, kan en <Link to="/carport" className="text-[#004FA4] font-medium underline hover:text-[#143463]">carport</Link> også indgå som en del af løsningen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Tilpas terrassen til grundens muligheder & fliser */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-16">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Terræn & Opbygning
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                Tilpas terrassen til grundens muligheder
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  Ikke to grunde er ens, og terrassens opbygning bør derfor tage udgangspunkt i forholdene på stedet. På en skrånende grund kan en træterrasse i flere niveauer være en god måde at udnytte højdeforskellene på. Det kan eksempelvis give plads til en spiseplads tæt på huset og et separat opholdsområde længere ude.
                </p>
                <p>
                  En terrasse med niveauskift kræver præcis opmåling og en stabil konstruktion. Højden har samtidig betydning for udsyn, adgang og forholdet til omgivelserne. Ønsker du samtidig mere privatliv omkring en hævet terrasse, kan et <Link to="/komposithegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">komposithegn</Link> eller et <Link to="/panelhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">panelhegn</Link> være med til at skabe en tydelig afgrænsning.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative border border-[#D6D3CE]/60 p-3 bg-white shadow-lg">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={imageUrl("/_assets/traeterrasse-have.webp", "width=800,quality=85,format=auto")}
                    alt="ny træterrasse - træterrasse i flere niveauer i have"
                    className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 bg-[#F3F4F7] mt-3 flex items-center justify-between text-xs text-[#143463]">
                  <span className="font-medium">Niveaudelt træterrasse</span>
                  <span className="text-[#4B515D]">Optimal udnyttelse af skrånende grund</span>
                </div>
              </div>
            </div>

          </div>

          {/* Undersektion: Træterrasse på fliser */}
          <div className="bg-[#F3F4F7] border border-[#D6D3CE]/60 p-8 sm:p-12">
            <div className="max-w-3xl space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                <i className="fa-sharp fa-solid fa-layer-group" aria-hidden="true"></i>
                <span>Underlag & Forberedelse</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-light text-[#143463]">
                Træterrasse på fliser – sådan vurderer vi underlaget
              </h3>
              <p className="text-base text-[#4B515D] font-light leading-relaxed">
                En træterrasse på fliser kan i nogle tilfælde være en god løsning, men de eksisterende fliser skal først vurderes. Underlaget skal være stabilt, og konstruktionen skal kunne opbygges, så vand kan ledes væk, og træet får den nødvendige ventilation.
              </p>
              <p className="text-base text-[#4B515D] font-light leading-relaxed">
                Højden ved døre og overgangen til haven spiller også en rolle. Hvis fliserne ligger ujævnt, eller der ikke er plads til en fornuftig opbygning, kan det være nødvendigt at ændre underlaget først. Vi vurderer derfor den konkrete grund frem for at tage udgangspunkt i én standardløsning.
              </p>
              <p className="text-base text-[#4B515D] font-light leading-relaxed">
                Skal terrassen afskærmes mod omgivelserne, kan valget af hegn tænkes ind allerede i planlægningen. Et <Link to="/smedejernshegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">smedejernshegn</Link> giver en mere åben afgrænsning, mens et <Link to="/betonhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">betonhegn</Link> kan være relevant, hvis du ønsker at begrænse arbejdet med den løbende pleje omkring terrassen.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sektion 4: Hvad koster en træterrasse? */}
      <section className="py-20 md:py-24 bg-[#143463] text-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF]">
                Pris & Beregning
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight leading-snug">
                Hvad koster en træterrasse?
              </h2>
              <div className="space-y-4 text-slate-200 font-light leading-relaxed text-base">
                <p>
                  Prisen på en træterrasse afhænger af den konkrete opgave. Du vil ofte møde forskellige træterrasse priser pr. kvadratmeter, men de fortæller ikke nødvendigvis, hvad netop din terrasse kommer til at koste.
                </p>
                <p>
                  Størrelse, træsort, terræn og den konstruktion, der skal bære brædderne, kan alle påvirke prisen. Det samme gælder, hvis der skal etableres trin, flere niveauer eller fjernes en eksisterende belægning.
                </p>
                <p>
                  Hvis du undersøger en ny træterrasse pris, giver det derfor bedst mening at tage udgangspunkt i den konkrete løsning frem for en generel kvadratmeterpris. Det samme gælder, når du ønsker et træterrasse tilbud. To terrasser med samme størrelse kan kræve meget forskelligt arbejde.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
                  <span>Ring {companyInfo.phone}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white/5 border border-white/10 p-8 space-y-6">
                <h3 className="text-lg font-medium text-white uppercase tracking-wider border-b border-white/10 pb-4">
                  Når vi skal beregne en træterrasse pris, ser vi blandt andet på:
                </h3>
                <ul className="space-y-4 text-sm text-slate-300 font-light">
                  {priceFactors.map((factor, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-none bg-[#004FA4] text-white flex items-center justify-center shrink-0 text-xs mt-0.5">
                        <i className={`fa-sharp fa-solid ${factor.icon}`} aria-hidden="true"></i>
                      </div>
                      <div>
                        <span className="font-medium text-white block">{factor.title}</span>
                        <span className="text-xs text-slate-300 font-light">{factor.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 5: Træterrasse ved hus - Fra første idé til en terrasse, du får brugt */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-16">
            
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="relative border border-[#D6D3CE]/60 p-3 bg-white shadow-lg">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={imageUrl("/_assets/traeterrasse-ved-hus.webp", "width=800,quality=85,format=auto")}
                    alt="Træterrasse ved hus - Træterrasse privat ved hus"
                    className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 bg-[#F3F4F7] mt-3 flex items-center justify-between text-xs text-[#143463]">
                  <span className="font-medium">Træterrasse ved hus</span>
                  <span className="text-[#4B515D]">Træterrasse privat ved hus</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Proces & Rådgivning
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                Fra første idé til en terrasse, du får brugt
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  Vi starter med at høre, hvordan du vil bruge området, og hvad der findes på grunden i dag. Derefter ser vi på mål, materialer og den opbygning, terrassen kræver. Du er med i dialogen gennem projektet, så den færdige løsning tager udgangspunkt i det, vi har aftalt.
                </p>
                <p>
                  En terrasse fungerer bedst, når den hænger sammen med resten af uderummet. Ligger boligen tæt på en trafikeret vej eller andre støjkilder, kan et <Link to="/stoejhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">støjhegn</Link> være relevant at tænke ind sammen med terrassen. På den måde kan indretningen af haven planlægges som en samlet løsning.
                </p>
                <p>
                  Adgangen mellem terrasse, have og indkørsel kan også tænkes ind fra begyndelsen. Ved et komposithegn kan en <Link to="/kompositlaage" className="text-[#004FA4] font-medium underline hover:text-[#143463]">kompositlåge</Link> skabe en naturlig passage, mens en <Link to="/smedejernslaage" className="text-[#004FA4] font-medium underline hover:text-[#143463]">smedejernslåge</Link> kan kombineres med andre typer afgrænsning omkring ejendommen.
                </p>
                <p>
                  Ved større indkørsler kan en <Link to="/skydeporte" className="text-[#004FA4] font-medium underline hover:text-[#143463]">skydeport</Link> være en praktisk del af den samlede indretning. På den måde kan terrasse, have, hegn og adgangsforhold planlægges med udgangspunkt i hele ejendommen.
                </p>
              </div>
            </div>

          </div>

          {/* Sektion 6: Træterrasser på Fyn, i Odense og resten af Danmark */}
          <div className="mt-16 bg-[#F3F4F7] border border-[#D6D3CE]/60 p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                  Lokal forankring & landsdækkende montage
                </span>
                <h3 className="text-2xl sm:text-3xl font-light text-[#143463]">
                  Træterrasser på Fyn, i Odense og resten af Danmark
                </h3>
                <p className="text-base text-[#4B515D] font-light leading-relaxed">
                  Hovendals holder til i Otterup nær Odense på Fyn, men vi løser opgaver i hele Danmark. Søger du efter træterrasse i Odense eller træterrasse på Fyn, har du derfor en lokal samarbejdspartner, samtidig med at vi også hjælper kunder i resten af landet.
                </p>
                <p className="text-base text-[#4B515D] font-light leading-relaxed">
                  Har du en idé, nogle mål eller spørgsmål til din nye terrasse? Fortæl os om projektet, så tager vi en snak om mulighederne og hjælper dig videre med et uforpligtende træterrasse tilbud.
                </p>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
                >
                  <span>Indhent tilbud</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#143463] text-[#143463] hover:bg-[#143463] hover:text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring {companyInfo.phone}</span>
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Inspirationsgalleri med Lightbox */}
      <section className="py-20 md:py-24 bg-white border-t border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Inspiration & Udførte Løsninger
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight mt-2">
              Se eksempler på terrasser og uderum
            </h2>
            <p className="text-sm text-[#4B515D] font-light mt-2">
              Klik på et billede for at se det i fuld størrelse.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setLightboxIndex(idx)}
                className="group relative cursor-pointer border border-[#D6D3CE]/60 bg-[#F3F4F7] overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={imageUrl(img.src, "width=600,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 bg-white">
                  <span className="text-[11px] font-medium text-[#004FA4] uppercase tracking-wider block mb-1">
                    {img.category}
                  </span>
                  <h4 className="font-medium text-[#143463] text-sm group-hover:text-[#004FA4] transition-colors leading-snug">
                    {img.title}
                  </h4>
                  <p className="text-xs text-[#4B515D] font-light mt-1 line-clamp-2">
                    {img.desc}
                  </p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 text-[#143463] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="fa-sharp fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/projekter"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group"
            >
              <span>Se alle vores udførte projekter og referencer</span>
              <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <LightboxModal
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
        />
      )}

      {/* Quote Section */}
      <QuoteSection />
    </>
  );
}
